/**
 * Screenshot for bug reports.
 *
 * Primary path is the Screen Capture API (getDisplayMedia): a real capture of
 * what the compositor actually painted. The previous html2canvas-pro path is
 * kept only as a fallback, because it is not a screenshot at all — it
 * re-implements CSS painting into a canvas from computed styles, so anything
 * its painter models imperfectly gets invented or displaced. That produced
 * blurred grey shapes over empty areas of the dashboard, which is worse than
 * cosmetic on a bug report: it shows the reader things that were never on the
 * reporter's screen.
 *
 * Two constraints shape the flow below:
 *   • getDisplayMedia needs transient user activation, so it must be the first
 *     await in the click handler — hence the picker opens before anything else.
 *   • The stream is live, so the Support panel has to be hidden AFTER the user
 *     has chosen a surface but BEFORE the frame is grabbed. That is what the
 *     beforeGrab/afterGrab hooks are for.
 *
 * Quality notes, since an inaccurate bug screenshot is worse than none:
 *   • The UA will happily hand back a downscaled 1080p-ish track unless you ask
 *     for more, so we request a high ideal and then push the track to its own
 *     reported maximum via applyConstraints.
 *   • Region Capture (cropTo) trims the stream to the page itself, so the shot
 *     contains the app rather than the app inside a picture of a browser.
 *   • Frames are written 1:1, never resampled.
 *   • Frames are grabbed off requestVideoFrameCallback rather than a fixed
 *     sleep, so we snap a frame the compositor has actually produced since the
 *     panel was hidden instead of guessing at the latency.
 */

export interface ScreenshotResult {
  blob: Blob;
  dataUrl: string;
  width: number;
  height: number;
}

export interface CaptureOptions {
  /** Hide app chrome that shouldn't appear in the shot. Awaited before grabbing. */
  beforeGrab?: () => void;
  /** Always runs, including on failure. */
  afterGrab?: () => void;
}

/** Thrown when the user dismisses the browser's screen picker. Not an error to surface. */
export class ScreenshotCancelled extends Error {
  constructor() {
    super('Screenshot cancelled');
    this.name = 'ScreenshotCancelled';
  }
}

// Applies to the DOM-render fallback ONLY. The real capture is never
// downscaled — see frameToCanvas. Here it bounds how large a canvas
// html2canvas is asked to rasterise, which is a cost, not a fidelity gain.
const MAX_DIMENSION = 2560;

function supportsDisplayCapture(): boolean {
  return typeof navigator !== 'undefined'
    && typeof navigator.mediaDevices?.getDisplayMedia === 'function';
}

async function canvasToResult(canvas: HTMLCanvasElement): Promise<ScreenshotResult> {
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      // No quality argument: PNG is lossless and toBlob ignores it.
      (b) => (b ? resolve(b) : reject(new Error('Failed to encode screenshot as PNG'))),
      'image/png',
    );
  });
  return { blob, dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
}

/**
 * Draw the frame 1:1. No downscaling: resampling a screenshot softens exactly
 * the thing it exists to show — small UI text and hairlines. It leaves as a
 * lossless PNG and is uploaded as a binary attachment rather than embedded in
 * the issue body, so the file size costs one upload, not the report.
 */
function frameToCanvas(video: HTMLVideoElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not create a drawing context for the screenshot');
  ctx.drawImage(video, 0, 0);
  return canvas;
}

async function captureViaDisplayMedia(opts: CaptureOptions): Promise<ScreenshotResult> {
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      audio: false,
      video: {
        // Bias the picker toward this tab rather than a whole screen.
        displaySurface: 'browser',
        // Without an explicit ask the UA commonly caps display capture around
        // 1080p regardless of the real display size.
        width: { ideal: 3840 },
        height: { ideal: 2160 },
      },
      // Chrome defaults to excluding the calling tab, which would make it
      // impossible to screenshot the very app being reported on.
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
      systemAudio: 'exclude',
      preferCurrentTab: true,
    } as DisplayMediaStreamOptions);
  } catch (err) {
    // Dismissing the picker throws NotAllowedError. That's a choice, not a fault.
    if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'AbortError')) {
      throw new ScreenshotCancelled();
    }
    throw err;
  }

  const [track] = stream.getVideoTracks();
  const video = document.createElement('video');

  try {
    // Push the track to whatever it says it can actually do. Best-effort: a
    // window or whole-screen surface may refuse, which is fine.
    try {
      const caps = track.getCapabilities?.();
      if (caps?.width?.max && caps?.height?.max) {
        await track.applyConstraints({ width: caps.width.max, height: caps.height.max });
      }
    } catch { /* keep whatever the UA gave us */ }

    // Region Capture: crop the stream to the page, dropping the browser's own
    // chrome. Only valid when the chosen surface IS this tab, so failures here
    // are expected and non-fatal.
    try {
      // CropTarget / cropTo are Chrome-only and absent from lib.dom.
      const w = window as unknown as { CropTarget?: { fromElement(e: Element): Promise<unknown> } };
      const cropTrack = track as unknown as { cropTo?: (t: unknown) => Promise<void> };
      if (w.CropTarget && typeof cropTrack.cropTo === 'function') {
        await cropTrack.cropTo(await w.CropTarget.fromElement(document.documentElement));
      }
    } catch { /* not a croppable surface */ }

    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();
    for (let i = 0; i < 30 && (video.videoWidth === 0 || video.videoHeight === 0); i++) {
      await new Promise((r) => requestAnimationFrame(r));
    }
    if (!video.videoWidth || !video.videoHeight) {
      throw new Error('The screen capture produced no image');
    }

    opts.beforeGrab?.();
    await waitForFreshFrames(video, 3);

    return await canvasToResult(frameToCanvas(video));
  } finally {
    opts.afterGrab?.();
    video.srcObject = null;
    stream.getTracks().forEach((t) => t.stop());
  }
}

/**
 * Resolve once the compositor has produced `count` new frames, so a DOM change
 * made just before is guaranteed to be in the one we grab. Falls back to a
 * timeout on browsers without requestVideoFrameCallback, and is itself capped
 * so a stalled track can never hang the capture.
 */
async function waitForFreshFrames(video: HTMLVideoElement, count: number): Promise<void> {
  // requestVideoFrameCallback is not in lib.dom's HTMLVideoElement yet.
  const rvfc = (video as unknown as {
    requestVideoFrameCallback?: (cb: () => void) => number;
  }).requestVideoFrameCallback;

  if (typeof rvfc !== 'function') {
    await new Promise((r) => setTimeout(r, 300));
    return;
  }
  await new Promise<void>((resolve) => {
    let seen = 0;
    const timer = setTimeout(resolve, 1000);
    const tick = () => {
      if (++seen >= count) { clearTimeout(timer); resolve(); return; }
      rvfc.call(video, tick);
    };
    rvfc.call(video, tick);
  });
}

/**
 * Fallback for runtimes with no Screen Capture API — notably the iOS/macOS
 * WKWebView shells. Known to be approximate: see the file header.
 */
async function captureViaDomRender(opts: CaptureOptions): Promise<ScreenshotResult> {
  // The -pro fork is required: classic html2canvas can't parse the modern CSS
  // colour functions (oklch / lab / color-mix) Tailwind v4 emits, and throws
  // mid-render instead of producing an image.
  const { default: html2canvas } = await import('html2canvas-pro');

  const dpr = window.devicePixelRatio || 1;
  const cappedDpr = Math.min(dpr, 2);
  const scale = window.innerWidth * cappedDpr > MAX_DIMENSION
    ? MAX_DIMENSION / window.innerWidth
    : cappedDpr;

  opts.beforeGrab?.();
  try {
    await new Promise((r) => requestAnimationFrame(r));
    await new Promise((r) => requestAnimationFrame(r));
    const canvas = await html2canvas(document.body, {
      scale,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: null,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      x: window.scrollX,
      y: window.scrollY,
      width: window.innerWidth,
      height: window.innerHeight,
    });
    return await canvasToResult(canvas);
  } finally {
    opts.afterGrab?.();
  }
}

export async function captureScreenshot(opts: CaptureOptions = {}): Promise<ScreenshotResult> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    throw new Error('Screenshot capture is only available in the browser');
  }
  return supportsDisplayCapture()
    ? captureViaDisplayMedia(opts)
    : captureViaDomRender(opts);
}
